import React, { useCallback, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import Card from './Card';
import SectionHeader from './SectionHeader';
import Loading from '../common/Loading';
import { withLanguage } from '../common/Texts';
import { getChart } from '../../utils/charts';
import { showLegacyJacquesChart, showVisxJacquesChart } from '../../config';
import BLUE_DOT from '../../images/blue-dot.png';
import TURQUESA_DOT from '../../images/turquesa-dot.png';
import { dashboardTokens as t } from './tokens';
import { toTitleCase } from '../../utils/toTitleCase';
import DashboardJacquesChart from './DashboardJacquesChart';
import DownloadSplitButton from '../DownloadSplitButton';

const CHART_WIDTH = 776;
const CHART_HEIGHT = 520;

function LegendDot({ color }) {
    return (
        <Box component="span" sx={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: color,
            mr: 0.75,
        }}/>
    );
}

export default function CapacityMap({
    language,
    company,
    loading,
    chartDots,
    dots,
    onDownload,
}) {
    const tr = withLanguage(language);
    const canvasRef = useRef(null);
    const tipCanvasRef = useRef(null);
    const dotsRef = useRef(dots);
    const blueDotRef = useRef(null);
    const turqDotRef = useRef(null);
    const chartImageRef = useRef({ src: null, img: null });
    // Incremented each time drawChart is called; lets stale onload callbacks
    // detect that a newer draw has already been scheduled and skip rendering.
    const drawGenRef = useRef(0);

    useEffect(() => { dotsRef.current = dots; }, [dots]);

    useEffect(() => {
        const blue = new Image();
        const turq = new Image();
        // Trigger a redraw once each dot image finishes loading so the first
        // cold-cache render doesn't show an empty chart.
        const redraw = () => {
            if (blueDotRef.current?.complete && turqDotRef.current?.complete) {
                drawGenRef.current += 1;
                const canvas = canvasRef.current;
                if (!canvas) return;
                const ctx = canvas.getContext('2d');
                const cached = chartImageRef.current;
                if (cached.img?.complete) {
                    ctx.clearRect(0, 0, CHART_WIDTH, CHART_HEIGHT);
                    ctx.drawImage(cached.img, 0, 0);
                    for (const dot of dotsRef.current) {
                        const dotImg = dot.altopot ? turqDotRef.current : blueDotRef.current;
                        if (dotImg?.complete) ctx.drawImage(dotImg, dot.x, dot.y);
                    }
                }
            }
        };
        blue.onload = redraw;
        turq.onload = redraw;
        blue.src = BLUE_DOT;
        turq.src = TURQUESA_DOT;
        blueDotRef.current = blue;
        turqDotRef.current = turq;
    }, []);

    const drawChart = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const src = getChart(language, company?.chart, true);

        // Snapshot the current dots so the closure is stable.
        const snapshotDots = chartDots;

        const render = (chartImg) => {
            ctx.clearRect(0, 0, CHART_WIDTH, CHART_HEIGHT);
            ctx.drawImage(chartImg, 0, 0);
            for (const dot of snapshotDots) {
                const dotImg = dot.altopot ? turqDotRef.current : blueDotRef.current;
                if (dotImg?.complete) {
                    ctx.drawImage(dotImg, dot.x, dot.y);
                }
            }
        };

        const cached = chartImageRef.current;
        if (cached.src === src && cached.img?.complete) {
            render(cached.img);
        } else {
            const gen = ++drawGenRef.current;
            const img = new Image();
            img.onload = () => {
                // Discard if a newer drawChart has already fired.
                if (gen !== drawGenRef.current) return;
                chartImageRef.current = { src, img };
                render(img);
            };
            img.src = src;
        }
    }, [language, company, chartDots]);

    useEffect(() => { drawChart(); }, [drawChart]);

    const TOOLTIP_FONT = '13px Montserrat, ui-sans-serif, sans-serif';
    const TOOLTIP_LINE_HEIGHT = 20;
    const TOOLTIP_PADDING_X = 8;

    const handleMouseMove = useCallback((e) => {
        e.preventDefault();
        const canvas = canvasRef.current;
        const tipCanvas = tipCanvasRef.current;
        if (!canvas || !tipCanvas) return;

        // The canvas element may be scaled by CSS (maxWidth: 100%).
        // Convert offsetX/Y (CSS-space) to canvas-buffer-space coordinates.
        const scaleX = CHART_WIDTH / canvas.offsetWidth;
        const scaleY = CHART_HEIGHT / canvas.offsetHeight;
        const mouseX = e.offsetX * scaleX;
        const mouseY = e.offsetY * scaleY;

        const DOT_HALF = 4; // blue-dot.png and turquesa-dot.png are 8×8px
        const dotsOver = [];
        for (const dot of dotsRef.current) {
            const dx = mouseX - (dot.x + DOT_HALF);
            const dy = mouseY - (dot.y + DOT_HALF);
            if (dx * dx + dy * dy < 100) { // radius 10px from centre
                dotsOver.push(dot);
            }
        }
        if (dotsOver.length === 0) {
            tipCanvas.style.left = '-1200px';
            tipCanvas.width = 0;
            tipCanvas.height = 0;
            return;
        }

        // Measure text width accurately using the canvas 2d API before resizing.
        const tipCtx = tipCanvas.getContext('2d');
        tipCtx.font = TOOLTIP_FONT;
        let maxTextWidth = 0;
        const labels = dotsOver.map((dot) => toTitleCase(dot.tip));
        for (const label of labels) {
            const w = tipCtx.measureText(label).width;
            if (w > maxTextWidth) maxTextWidth = w;
        }

        const newWidth = Math.ceil(maxTextWidth) + TOOLTIP_PADDING_X * 2;
        const newHeight = dotsOver.length * TOOLTIP_LINE_HEIGHT;

        // Resize only when dimensions change to avoid unnecessary layout clears.
        if (tipCanvas.width !== newWidth) tipCanvas.width = newWidth;
        if (tipCanvas.height !== newHeight) tipCanvas.height = newHeight;

        const dot0 = dotsOver[0];
        tipCanvas.style.left = `${dot0.x / scaleX}px`;
        tipCanvas.style.top = `${(dot0.y / scaleY) - 40}px`;

        // Re-set font after resize (resize clears canvas state).
        tipCtx.font = TOOLTIP_FONT;
        tipCtx.fillStyle = '#0F172A';
        tipCtx.clearRect(0, 0, newWidth, newHeight);
        labels.forEach((label, i) => {
            tipCtx.fillText(label, TOOLTIP_PADDING_X, (i + 1) * TOOLTIP_LINE_HEIGHT - 5);
        });
    }, []);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return undefined;
        canvas.addEventListener('mousemove', handleMouseMove);
        return () => canvas.removeEventListener('mousemove', handleMouseMove);
    }, [handleMouseMove]);

    return (
        <Card sx={{ position: 'relative' }}>
            <Loading loading={loading} language={language}/>
            <Box sx={{ opacity: loading ? 0.1 : 1 }}>
                <SectionHeader
                    title={tr('capacity_map')}
                    subtitle={tr('capacity_map_subtitle')}
                    action={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1.5, fontSize: 12, color: t.color.fgMuted }}>
                                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                    <LegendDot color={t.color.standard}/> {tr('standard')}
                                </Box>
                                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                    <LegendDot color={t.color.hipo}/> {tr('high_potential')}
                                </Box>
                            </Box>
                            <DownloadSplitButton
                                onDownload={onDownload}
                                label={tr('download_csv')}
                                csvLabel={tr('download_as_csv')}
                                xlsxLabel={tr('download_as_xlsx')}
                                buttonSx={{
                                    color: t.color.fg,
                                    borderColor: t.color.border,
                                    backgroundColor: t.color.surface,
                                    '&:hover': {
                                        borderColor: t.color.primary,
                                        color: t.color.primary,
                                        backgroundColor: t.color.primarySoft,
                                    },
                                }}
                                arrowSx={{
                                    borderColor: t.color.border,
                                    backgroundColor: t.color.surface,
                                    '&:hover': {
                                        borderColor: t.color.primary,
                                        backgroundColor: t.color.primarySoft,
                                    },
                                }}
                            />
                        </Box>
                    }
                />
                {showLegacyJacquesChart && (
                    <Box sx={{
                        position: 'relative',
                        overflowX: 'auto',
                    }}>
                        <Box sx={{
                            position: 'relative',
                            width: CHART_WIDTH,
                            height: CHART_HEIGHT,
                            maxWidth: '100%',
                        }}>
                            <canvas
                                ref={canvasRef}
                                width={CHART_WIDTH}
                                height={CHART_HEIGHT}
                                style={{ display: 'block', maxWidth: '100%' }}
                            />
                            <canvas
                                ref={tipCanvasRef}
                                width={0}
                                height={0}
                                style={{
                                    position: 'absolute',
                                    left: '-1200px',
                                    pointerEvents: 'none',
                                    background: t.color.surface,
                                }}
                            />
                        </Box>
                    </Box>
                )}
                {showVisxJacquesChart && (
                    <Box sx={{
                        mt: showLegacyJacquesChart ? 3 : 0,
                        pt: showLegacyJacquesChart ? 3 : 0,
                        borderTop: showLegacyJacquesChart ? `1px solid ${t.color.divider}` : 'none',
                    }}>
                        <DashboardJacquesChart
                            dots={chartDots}
                            companyChart={company?.chart}
                            language={language}
                        />
                    </Box>
                )}
            </Box>
        </Card>
    );
}
